import { FolderMetadata, FolderStatus, MetadataFieldKeys } from "types";
import { useCallback, useMemo, useState, useEffect } from "react";

// Helper to check if folder matches given values for a key
const isMatch = ([key, vals]: [string, string[]], folder: FolderMetadata) => {
  // Handle special fields that are not in metadata
  let meta: string | string[] | undefined;
  if (key === "status") {
    meta = folder.status;
  } else if (key === "folderName") {
    meta = folder.folder_name;
  } else {
    meta = folder.metadata?.[key];
  }

  if (Array.isArray(meta)) {
    return vals.every((v) => meta!.includes(v));
  }

  return vals.includes(String(meta));
};

// Helper to check if folder matches all applied filters
const matchesAllFilters = (
  folder: FolderMetadata,
  filterState: Record<string, string[]>,
) => {
  return Object.entries(filterState).every(
    ([key, vals]) => vals.length === 0 || isMatch([key, vals], folder),
  );
};

// get status tooltip messages

const statusTooltipMessages: Record<Exclude<FolderStatus, "valid">, string> = {
  invalid: "This folder can't be selected because it's invalid.",
  uploading: "This folder is still not approved as valid for selection.",
  validating: "This folder is still not approved as valid for selection.",
};

// Metadata fields that get applied when a folder is selected
const METADATA_FIELDS = Object.values(MetadataFieldKeys);

export default function useFolderSelection(
  folders: FolderMetadata[],
  onChange: (updated: FolderMetadata[]) => void,
  currentBatchId?: string,
) {
  // Tracks all selected filter values (including constraints)
  const [filterState, setFilterState] = useState<Record<string, string[]>>({});

  // IDs of currently selected folders
  const selectedRowKeys = useMemo(
    () => folders.filter((f) => f.selected).map((f) => f.id),
    [folders],
  );

  // Apply filters when user navigates back from preview step
  useEffect(() => {
    if (selectedRowKeys.length > 0 && Object.keys(filterState).length === 0) {
      // Find the first selected folder to restore its filter constraints
      const selectedFolder = folders.find((f) => f.selected);
      if (selectedFolder) {
        const newFilters: Record<string, string[]> = {};

        // Apply metadata filters
        if (selectedFolder.metadata) {
          METADATA_FIELDS.forEach((field) => {
            const value = selectedFolder.metadata![field];
            if (value) {
              newFilters[field] = Array.isArray(value)
                ? value.map(String)
                : [String(value)];
            }
          });
        }

        // Apply status filter
        newFilters["status"] = [selectedFolder.status];

        setFilterState(newFilters);
      }
    }
  }, [folders, selectedRowKeys.length, filterState]);

  // Folders with checkbox disabled - only allow valid/ready folders that match current filters
  const checkboxDisabledKeys = useMemo(() => {
    return folders
      .filter((folder) => {
        // Must have valid status
        if (!["valid"].includes(folder.status)) {
          return true; // disabled
        }

        if (currentBatchId) {
          if (!folder.batch_id || folder.batch_id !== currentBatchId) {
            return true;
          }
        }

        // If filters are applied, folder must match them to be selectable
        if (Object.keys(filterState).length > 0) {
          return !matchesAllFilters(folder, filterState);
        }

        return false; // enabled
      })
      .map((folder) => folder.id);
  }, [folders, filterState, currentBatchId]);

  // Tooltip data for disabled checkboxes
  const checkboxTooltipData = useMemo(() => {
    const tooltipMap: Record<string, string> = {};

    folders.forEach((folder) => {
      if (folder.status !== "valid") {
        const message = statusTooltipMessages[folder.status];
        if (message) tooltipMap[folder.id] = message;
      } else if (currentBatchId) {
        if (!folder.batch_id || folder.batch_id !== currentBatchId) {
          tooltipMap[folder.id] = "This folder already exists in dataset.";
        }
      }
    });

    return tooltipMap;
  }, [folders, currentBatchId]);

  // Folders with grayed text - folders that don't match current filters
  const disabledRowKeys = useMemo(() => {
    if (Object.keys(filterState).length === 0) {
      return []; // No filters applied, no rows grayed
    }

    return folders
      .filter((folder) => !matchesAllFilters(folder, filterState))
      .map((folder) => folder.id);
  }, [folders, filterState]);

  // Update selection state
  const handleSelectionChange = useCallback(
    (keys: React.Key[]) => {
      // Update folder selections
      const updated = folders.map((folder) => ({
        ...folder,
        selected: keys.includes(folder.id),
      }));

      if (keys.length > 0) {
        // Folder selected: Apply its values to filters
        const selectedFolder = updated.find((f) => f.selected);
        if (selectedFolder) {
          const newFilters: Record<string, string[]> = {};

          // Apply metadata filters
          if (selectedFolder.metadata) {
            METADATA_FIELDS.forEach((field) => {
              const value = selectedFolder.metadata![field];
              if (value) {
                newFilters[field] = Array.isArray(value)
                  ? value.map(String)
                  : [String(value)];
              }
            });
          }

          // Apply status filter
          newFilters["status"] = [selectedFolder.status];

          setFilterState(newFilters);
        }
      } else if (selectedRowKeys.length > 0) {
        // All folders deselected: clear all filters
        setFilterState({});
      }
      // If multiple folders were selected and one is deselected, keep filters

      onChange(updated);
    },
    [folders, onChange, selectedRowKeys.length],
  );

  // Update user-applied filters
  const handleFilterChange = useCallback(
    (changed: Record<string, string[] | null>) => {
      // If user manually changes filters, deselect all folders first
      if (selectedRowKeys.length > 0) {
        const updated = folders.map((folder) => ({
          ...folder,
          selected: false,
        }));
        onChange(updated);
      }

      setFilterState((prevState) => {
        const nextState = { ...prevState };

        // Apply changes: update or remove filters based on the changed values
        Object.entries(changed).forEach(([key, values]) => {
          if (values && values.length > 0) {
            nextState[key] = values;
          } else {
            delete nextState[key]; // Remove filter if no values selected
          }
        });

        return nextState;
      });
    },
    [selectedRowKeys.length, folders, onChange],
  );

  // Compute visible folders based on applied filters
  const filteredFolders = useMemo(() => {
    // If no filters applied, show all folders
    if (Object.keys(filterState).length === 0) {
      return folders;
    }

    // Apply filtering logic
    return folders.filter((folder) => {
      if (selectedRowKeys.length > 0) {
        // Folder selected: AND logic (must match ALL filters exactly)
        return matchesAllFilters(folder, filterState);
      } else {
        // No folder selected: OR logic (match ANY filter type)
        return Object.entries(filterState).some(([key, vals]) =>
          isMatch([key, vals], folder),
        );
      }
    });
  }, [folders, filterState, selectedRowKeys.length]);

  // Determine if select all should be shown
  const showSelectAll = useMemo(() => {
    // Show select all only when filters are applied (i.e., a folder is selected)
    return Object.keys(filterState).length > 0;
  }, [filterState]);

  // Get selectable folders from currently visible folders
  const selectableFolders = useMemo(() => {
    return filteredFolders.filter(
      (folder) =>
        !checkboxDisabledKeys.includes(folder.id) &&
        !disabledRowKeys.includes(folder.id),
    );
  }, [filteredFolders, checkboxDisabledKeys, disabledRowKeys]);

  // Handle select all functionality
  const handleSelectAll = useCallback(() => {
    // Select all visible and selectable folders
    const selectableKeys = selectableFolders.map((folder) => folder.id);
    handleSelectionChange(selectableKeys);
  }, [selectableFolders, handleSelectionChange]);

  return {
    selectedRowKeys,
    disabledRowKeys,
    checkboxDisabledKeys,
    checkboxTooltipData,
    filteredFolders,
    showSelectAll,
    filterState,
    handleSelectionChange,
    handleFilterChange,
    handleSelectAll,
  };
}
